import { AgentDesktopState } from '@agent/agent-interface/agent-desktop-routing.module';
import { BasketAction, fromMailBasket, MailBasketAction } from '@agent/core/basket';
import { Load, ManualRefresh, OpenRequest, Refresh } from '@agent/core/basket/basket.action';
import { BasketCategoryModel } from '@agent/core/basket/basket.model';
import { ChangeQueryFilter } from '@agent/core/basket/mail/mail-basket.action';
import { EpticaRequestType } from '@agent/core/channel-digit/mail/eptica/models/eptica.request.interfaces';
import { TimelineEntry } from '@commons/history/timeline/models/timeline-entry';
import { debounceTime, EMPTY, filter, ignoreElements, map, merge, mergeMap, Observable, of, switchMap, tap, withLatestFrom } from 'rxjs';

import { Action, AgentStatus, ChannelType, isNotNullOrUndefined, toActionError } from '@contact/upgrade';

import { SW } from '../models/basket';
// import { ChangeBookmark } from "@agent/core/basket/mail/mail-basket.action";
import { ActionTypes as MailActionType, ChangeBookmark, ResetQueryFilter } from './mail-basket.action';
import { MailCategoryModel, MailLabelModel, RequestModel } from './mail-basket.model';

export function openRequestEffect({
  mailBasketService,
  basketStore,
  agentUserFacade,
  toasterFacade,
  $i18next,
  modalService,
  contactHistoryService,
}: SW): Observable<Action> {
  return basketStore.actions$.pipe(
    // filter action Open
    filter<OpenRequest<RequestModel>>(action => action.type === BasketAction.ActionTypes.OPEN_REQUEST),
    withLatestFrom(
      agentUserFacade.status$,
      basketStore.pipe(fromMailBasket.selectCategorySelected()),
      basketStore.pipe(fromMailBasket.selectLabelSelected())
    ),
    switchMap(([{ request }, status, category, label]) => {
      // if agent status is not available or working
      if (status !== AgentStatus.AVAILABLE && status !== AgentStatus.WORKING) {
        toasterFacade.notification({
          message: 'agent:toast.become_available',
          duration: 5000,
        });
        return EMPTY;
      }
      if (label === EpticaRequestType.DEDICATED_AGENT_UNASSIGNED) {
        if (request.state === 'closed') {
          return contactHistoryService.searchByEpticaRequestId(request.id).pipe(
            isNotNullOrUndefined(),
            tap(entry => {
              const modalParam = {
                keyboard: false,
                backdrop: false,
                fullscreen: true,
                ctrlKeyboard: true,
                component: 'contactContactViewHistoPopin',
                title: $i18next.t('agent:contact.info.popin.title'),
                modalId: `${$i18next.t('agent:contact.info.popin.title')} ${entry.contactLastName} ${entry.contactFirstName}`,
                resolve: {
                  selectContactEntry: () => TimelineEntry.fromContactEntry(entry),
                },
              };
              modalService.open(modalParam);
            })
          );
        }
      }
      // otherwise we can ask to service open request
      return mailBasketService.openRequest(request, category);
    }),
    // don't resend action
    ignoreElements()
  );
}

export function loadGlobalEffect({ router, mailBasketService, basketStore }: SW): Observable<Action> {
  return basketStore.actions$.pipe(
    // filter action LOAD
    filter<Load>(action => action.type === BasketAction.ActionTypes.LOAD),
    // if agent is not on basket and load is a auto refresh
    withLatestFrom(router.currentUrl$),
    filter(([action, url]) => action.autoRefresh && url?.indexOf(AgentDesktopState.BASKET) === -1),
    // on each load action
    switchMap(() =>
      // refresh counter personal/common
      mailBasketService.globalCounter().pipe(
        // map to success load action
        map(result => new MailBasketAction.SuccessLoad(ChannelType.MAIL, result.label, result))
      )
    )
  );
}

export function saveFilterEffect({ mailBasketService, basketStore }: SW): Observable<Action> {
  return basketStore.actions$.pipe(
    // filter action LOAD
    filter<ChangeQueryFilter>(action => action.type === MailActionType.CHANGE_QUERY_FILTER),
    // on each load action
    switchMap(action => mailBasketService.saveFilters(action.filters)),
    // don't resend action
    ignoreElements()
  );
}

export function resetFilterEffect({ mailBasketService, basketStore }: SW): Observable<Action> {
  return basketStore.actions$.pipe(
    // filter action RESET
    filter<ResetQueryFilter>(action => action.type === MailActionType.RESET_QUERY_FILTER),
    // on each reset action
    switchMap(() => mailBasketService.saveFilters({})),
    // don't resend action
    ignoreElements()
  );
}

export function saveSortEffect({ mailBasketService, basketStore }: SW): Observable<Action> {
  return basketStore.actions$.pipe(
    // filter action REFRESH
    filter<Refresh>(action => action.type === BasketAction.ActionTypes.REFRESH),
    filter<Refresh>(action => action.refreshType === 'manual'),
    filter<ManualRefresh>(action => !!action.sortKey),

    debounceTime(200),
    withLatestFrom(basketStore.pipe(fromMailBasket.selectCategories<BasketCategoryModel>()), (_, categories) => categories),
    // on each refresh action
    switchMap((categories: BasketCategoryModel[]) => mailBasketService.saveSort(categories)),
    // don't resend action
    ignoreElements()
  );
}

export function loadBasketEffect({ router, mailBasketService, basketStore }: SW): Observable<Action> {
  return basketStore.actions$.pipe(
    // filter action LOAD
    filter<Load>(action => action.type === BasketAction.ActionTypes.LOAD),
    // if agent is not on basket or load is not a auto refresh
    withLatestFrom(router.currentUrl$),
    filter(([action, url]) => !action.autoRefresh || url?.indexOf(AgentDesktopState.BASKET) !== -1),
    // filter if current channel is MAIL
    filter(([action]) => action.channel === ChannelType.MAIL),
    // on each load action
    switchMap(([action]) =>
      of({}).pipe(
        // retrieve current mail basket modedl
        withLatestFrom(
          basketStore.pipe(fromMailBasket.selectMailBasket()),
          basketStore.pipe(fromMailBasket.selectLabelByType<MailLabelModel>(action.label)),
          basketStore.pipe(fromMailBasket.selectCategoryByLabel<MailCategoryModel>(action.label))
        ),
        // refresh request + counter state
        mergeMap(([_, channelModel, labelModel, categoryModel]) =>
          merge(
            // update requests basket
            mailBasketService.getRequests({
              ...channelModel,
              ...labelModel,
              ...categoryModel,
            }),
            // update basket counter
            mailBasketService.basketCounter(channelModel)
          )
        ),
        // map to success load action
        map(result => new MailBasketAction.SuccessLoad(action.channel, result.label, result))
      )
    )
  );
}

export function changeBookmarkEffect({ mailBasketService, basketStore }: SW): Observable<Action> {
  return basketStore.actions$.pipe(
    filter<ChangeBookmark>(action => action.type === MailActionType.CHANGE_BOOKMARK),
    switchMap(action =>
      mailBasketService.changeBookmark(action.requestId, action.bookmark).pipe(
        // success case
        map(() => new MailBasketAction.ChangeBookmarkSuccess(action.requestId, action.bookmark)),
        // failure case
        toActionError(err => new MailBasketAction.ChangeBookmarkFail(action.requestId, action.bookmark, err))
      )
    )
  );
}
